import paper from "paper";
import {
  chunk,
  countBy,
  each,
  filter,
  flatMap,
  flatten,
  go,
  map,
} from "fxjs/es";

export const calculatePathOffset = (path, offset, join) => {
  let non_SI_path = getNonSIPath(path);
  let result = non_SI_path;
  if (non_SI_path instanceof paper.Path) {
    result = offsetSimpleShape(non_SI_path, offset, join);
  } else {
    throw new Error("Compound path 는 offset 을 허용하지 않습니다.");
  }
  return result;
};

const getNonSIPath = (path) => {
  //united boolean operation: clone 된 new object 가 반환
  let new_path = path.closed
    ? path.unite(path, { insert: false })
    : path.clone();
  new_path = removeSmallPath(new_path);
  return new_path;
};

const removeSmallPath = (path, ignore_area_threshold = 0.001) => {
  const ignore_area = Math.abs(path.area * ignore_area_threshold);
  if (path instanceof paper.CompoundPath) {
    path.children
      .filter((c) => Math.abs(c.area) < ignore_area)
      .forEach((c) => c.remove());
    return reduceSingleChildCompoundPath(path);
  }
  return path;
};

const reduceSingleChildCompoundPath = (path) => {
  if (path.children.length === 1) {
    path = path.children[0];
    path.remove();
  }
  return path;
};

const preparePath = (path, offset) => {
  const source = path.clone({ insert: false, deep: true });
  source.flatten(offset / 25);
  //Reduces the path by removing curves that have a length of 0, and unnecessary segments between two collinear flat curves.
  source.reduce();
  if (!path.clockwise) {
    source.reverse();
    offset = offset * -1;
  }
  return { source, offset_len: offset };
};

const offsetSegment = (segment, curve, handle_norm_vec, offset) => {
  const is_first_segment = segment.curve === curve;
  const offset_norm_vec = curve
    .getNormalAtTime(is_first_segment ? 0 : 1)
    .multiply(offset);
  const offset_segment = new paper.Segment(segment.point.add(offset_norm_vec));

  const handle = is_first_segment ? "handleOut" : "handleIn";
  offset_segment[handle] = segment[handle].add(
    handle_norm_vec.subtract(offset_norm_vec).divide(2)
  );
  return offset_segment;
};

const getOffsetSegmentsFromCurve = (curve, handle_norm_vec, offset) =>
  map(
    (seg) => offsetSegment(seg, curve, handle_norm_vec, offset),
    [curve.segment1, curve.segment2]
  );

const getAdaptiveOffsetSegments = (curve, offset) => {
  const p_zero = new paper.Point(0, 0);
  const cp_a = curve.segment1.handleOut.add(curve.segment1.point);
  const cp_b = curve.segment2.handleIn.add(curve.segment2.point);
  const control_points_line = new paper.Curve(cp_a, p_zero, p_zero, cp_b);
  const offset_segments = getOffsetSegmentsFromCurve(
    curve,
    control_points_line.getNormalAtTime(0.5).multiply(offset),
    offset
  );
  const offset_curve = new paper.Curve(...offset_segments);
  const self_itx = offset_curve.getIntersections(offset_curve);
  if (self_itx.length === 0) {
    const error_threshold = Math.min(1, Math.abs(offset) / 10);
    // const error_check_time = curve.getTimesWithTangent(cp_b.subtract(cp_a));
    const error_check_time = 0.5;
    const mid_offset_len = offset_curve
      .getPointAtTime(error_check_time)
      .getDistance(curve.getPointAtTime(error_check_time));
    if (Math.abs(mid_offset_len - Math.abs(offset)) > error_threshold) {
      const split_curve = curve.divideAtTime(error_check_time);
      if (split_curve) {
        return getAdaptiveOffsetSegments(curve, offset).concat(
          getAdaptiveOffsetSegments(split_curve, offset)
        );
      }
    }
  }
  return offset_segments;
};

const makeRoundJoin = (seg_a, seg_b, seg_origin, radius) => {
  const vec_a = seg_a.point.subtract(seg_origin.point);
  const vec_b = seg_b.point.subtract(seg_origin.point);
  const through_point = vec_a
    .add(vec_b)
    .normalize(Math.abs(radius))
    .add(seg_origin.point);
  const arc = new paper.Path.Arc({
    from: seg_a.point,
    to: seg_b.point,
    through: through_point,
    insert: false,
  });
  seg_a.handleOut = arc.firstSegment.handleOut;
  seg_b.handleIn = arc.lastSegment.handleIn;
  return arc.segments.length === 3 ? arc.segments[1] : null;
};

const connectAdjacentBezier = (
  off_segs_ab_prev,
  off_segs_ab_next,
  origin_seg,
  join,
  offset
) => {
  if (origin_seg.isSmooth()) {
    off_segs_ab_next[0].handleOut = off_segs_ab_next[0].handleOut.project(
      origin_seg.handleOut
    );
    off_segs_ab_next[0].handleIn = off_segs_ab_prev[1].handleIn.project(
      origin_seg.handleIn
    );
    off_segs_ab_next[0].point = off_segs_ab_next[0].point
      .add(off_segs_ab_prev[1].point)
      .divide(2);
    off_segs_ab_prev.pop();
  } else {
    //handle 이 smooth 가 아닌 경우 로직
    const curve_a = new paper.Curve(...off_segs_ab_prev);
    const curve_b = new paper.Curve(...off_segs_ab_next);
    const curve_itx = curve_a.getIntersections(curve_b);
    const distance = off_segs_ab_prev[1].point.getDistance(
      off_segs_ab_next[0].point
    );
    if (curve_itx.length === 0) {
      if (distance > Math.abs(offset) * 0.1) {
        //connect path round
        const mid_through_arc_point = makeRoundJoin(
          off_segs_ab_prev[1],
          off_segs_ab_next[0],
          origin_seg,
          offset
        );
        mid_through_arc_point && off_segs_ab_prev.push(mid_through_arc_point);
      } else {
        off_segs_ab_next[0].handleIn = off_segs_ab_prev[1].handleIn;
        off_segs_ab_prev.pop();
      }
    } else {
      const second1 = curve_a.divideAt(curve_itx[0]);
      if (second1) {
        const join = second1.segment1;
        const second2 = curve_b.divideAt(curve_b.getIntersections(curve_a)[0]);
        join.handleOut = second2
          ? second2.segment1.handleOut
          : off_segs_ab_next[0].handleOut;
        off_segs_ab_prev.pop();
        off_segs_ab_next[0] = join;
      } else {
        off_segs_ab_next[0].handleIn = off_segs_ab_prev[1].handleIn;
        off_segs_ab_prev.pop();
      }
    }
  }
};

const connectBeziers = (offset_segments_ab_arr, join, source, offset) => {
  const original_segments_arr = source.segments;
  const first_offset_segments_ab = offset_segments_ab_arr[0].slice();
  const len = offset_segments_ab_arr.length;
  for (let i = 0; i < len - 1; i++) {
    connectAdjacentBezier(
      offset_segments_ab_arr[i],
      offset_segments_ab_arr[i + 1],
      original_segments_arr[i + 1],
      join,
      offset
    );
  }
  if (source.closed) {
    connectAdjacentBezier(
      offset_segments_ab_arr[len - 1],
      first_offset_segments_ab,
      original_segments_arr[0],
      join,
      offset
    );
    //???
    offset_segments_ab_arr[0][0] = first_offset_segments_ab[0];
  }
  return offset_segments_ab_arr;
};

const isSameDirection = (partial_path, full_path) => {
  const offset1 = partial_path.segments[0].location.offset;
  const offset2 =
    partial_path.segments[
      Math.max(1, Math.floor(partial_path.segments.length / 3))
    ].location.offset;
  const sampleOffset = (offset1 + offset2) / 3;
  const originOffset1 = full_path.getNearestLocation(
    partial_path.getPointAt(sampleOffset)
  ).offset;
  const originOffset2 = full_path.getNearestLocation(
    partial_path.getPointAt(2 * sampleOffset)
  ).offset;
  return originOffset1 < originOffset2;
};

const removeIntersection = (path) => {
  if (path.closed) {
    const new_path = path.unite(path, { insert: false });
    if (new_path instanceof paper.CompoundPath) {
      go(
        new_path.children,
        filter((c) => {
          if (c.segments.length > 1) {
            return !isSameDirection(c, path);
          } else {
            return true;
          }
        }),
        each((c) => c.remove())
      );
      return reduceSingleChildCompoundPath(new_path);
    }
  }
  return path;
};

export const getAllSegments = (path) => {
  return go(
    path.children || [path],
    flatMap((path) => path.segments.slice())
  );
};

const removeOutsiderSegments = (offset_len, new_path, original_path) => {
  go(
    getAllSegments(new_path),
    filter((s) => {
      //원본 path 에서 밖으로 나간 segment 제거
      if (!original_path.contains(s.point)) {
        return true;
      }
      const nn_distance = original_path
        .getNearestLocation(s.point)
        .getDistance(s.point);
      const error_permit = 0.1;
      if (
        nn_distance > Math.abs(offset_len) * (1 + error_permit) ||
        nn_distance < Math.abs(offset_len) * (1 - error_permit)
      ) {
        return true;
      }
    }),
    each((s) => {
      s.remove();
    })
  );
};

const removeOutsiderPath = ({ original_path, offset, cpd_path }) => {
  if (cpd_path instanceof paper.CompoundPath) {
    const outsider_decision_ratio = 0.3;
    go(
      cpd_path.children,
      filter((p) => {
        const inout = go(
          p.segments,
          countBy((s) => {
            return original_path.contains(s.point) ? "insider" : "outsider";
          })
        );
        const is_offset_dir_outside = offset / Math.abs(offset) === 1;
        if (is_offset_dir_outside) {
          // 바깥쪽 방향 offset
          // 바깥 점이 아예 없거나, 안 쪽 점 비율이 일정 이상인 경우 제거
          return (
            inout["outsider"] === undefined ||
            inout["insider"] / inout["outsider"] > outsider_decision_ratio
          );
        } else {
          return (
            inout["insider"] === undefined ||
            inout["outsider"] / inout["insider"] > outsider_decision_ratio
          );
        }
      }),
      each((p) => p.remove())
    );
  }
  return cpd_path;
};

const removeIncorrectPath = ({ original_path, cpd_path, offset_len }) => {
  if (cpd_path instanceof paper.CompoundPath) {
    const error_permit = 0.1;
    const path_remove_decision_ratio = 0.5;
    go(
      cpd_path?.children,
      filter((p) => {
        const near_far = go(
          p.segments,
          countBy((s) => {
            const nn_dis = original_path
              .getNearestPoint(s.point)
              .getDistance(s.point);
            if (
              Math.abs(nn_dis) > Math.abs(offset_len) * (1 + error_permit) ||
              Math.abs(nn_dis) < Math.abs(offset_len) * (1 - error_permit)
            ) {
              return "offset_error";
            } else {
              return "offset_correct";
            }
          })
        );
        return (
          near_far["offset_correct"] === undefined ||
          near_far["offset_error"] / near_far["offset_correct"] >
            path_remove_decision_ratio
        );
      }),
      each((p) => p.remove())
    );
  }
  return cpd_path;
};

const offsetSimpleShape = (path, offset, join) => {
  const { source, offset_len } = preparePath(path, offset);
  const offset_segments_arr = go(
    source.curves.slice(),
    flatMap((curve) => {
      return getAdaptiveOffsetSegments(curve, offset_len);
    }),
    chunk(2),
    (offset_segments_ab_arr) =>
      connectBeziers(offset_segments_ab_arr, join, source, offset_len),
    flatten
  );

  let offset_path = go(
    new paper.Path({
      segments: offset_segments_arr,
      insert: false,
      closed: path.closed,
    }),
    (path) => removeIntersection(path),
    (path) => removeSmallPath(path),
    (cpd_path) =>
      removeOutsiderPath({
        original_path: path,
        offset: offset_len,
        cpd_path,
      }),
    (path) => path.unite(path, { insert: false }),
    (cpd_path) =>
      removeIncorrectPath({
        original_path: path,
        offset: offset_len,
        cpd_path,
      })
  );

  // let new_path = removeIntersection(offset_path);
  // // let new_path = offset_path;
  //
  // new_path = removeSmallPath(new_path);
  //
  // removingOutsiderPath({
  //   original_path: path,
  //   offset: offset_len,
  //   cpd_path: new_path,
  // });

  // new_path = new_path.unite(new_path, { insert: false });

  if (
    source.closed &&
    ((source.clockwise && offset_len < 0) ||
      (!source.clockwise && offset_len > 0))
  ) {
    removeOutsiderSegments(offset_len, offset_path, path);
  }

  // =============
  //removing anticlockwise path
  // go(
  //   offset_path.children || offset_path,
  //   filter((p) => {
  //     return !p.clockwise;
  //   }),
  //   each((p) => {
  //     p.remove();
  //   })
  // );

  // go(
  //   new_path.children || new_path,
  //   filter((p) => {
  //     return !isSameDirection(p, path);
  //   }),
  //   each((p) => {
  //     p.remove();
  //   })
  // );

  // const self_itx = offset_path.getIntersections(offset_path);
  // if (self_itx.length) {
  //   go(
  //     self_itx.slice(),
  //     each((cl) => {
  //       cl?.segment?.remove();
  //     })
  //   );
  // }

  // return offset_path;
  // new_path.reduce({});

  // if (
  //   source.closed &&
  //   ((source.clockwise && offset_len < 0) ||
  //     (!source.clockwise && offset_len > 0))
  // ) {
  //   removeOutsiders(offset_len, new_path, path);
  // }
  //
  // if (source.clockwise !== path.clockwise) {
  //   new_path.reverse();
  // }
  // return normalize(new_path);
  return offset_path;
};
